import React, { useState, useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { useHowieAnalytics } from '@/hooks/useHowieAnalytics';
import { useHowieGraphData } from './hooks/useHowieGraphData';
import { HowieGalaxyView } from './HowieGalaxyView';
import HowieConceptPanel from './HowieConceptPanel';
import { BG_COLOR } from './constants';
import type { HowieKGState } from './types';

export const HowieKnowledgeGraphShell: React.FC = () => {
  const graphData = useHowieGraphData();
  const { track } = useHowieAnalytics();

  const [state, setState] = useState<HowieKGState>({
    viewLevel: 'galaxy',
    selectedCanonicalId: null,
  });

  const selectedCanonical = useMemo(
    () => graphData.canonicals.find((c) => c.id === state.selectedCanonicalId) ?? null,
    [graphData.canonicals, state.selectedCanonicalId],
  );

  const selectedDomainIndex = useMemo(
    () =>
      state.selectedCanonicalId
        ? (graphData.canonicalDomainIndex.get(state.selectedCanonicalId) ?? 0)
        : 0,
    [graphData.canonicalDomainIndex, state.selectedCanonicalId],
  );

  const selectedCanonicalPoles = useMemo(
    () =>
      state.selectedCanonicalId
        ? (graphData.canonicalPoles.get(state.selectedCanonicalId) ?? [])
        : [],
    [graphData.canonicalPoles, state.selectedCanonicalId],
  );

  const selectedRelatedConcepts = useMemo(
    () =>
      state.selectedCanonicalId
        ? (graphData.relatedConcepts.get(state.selectedCanonicalId) ?? [])
        : [],
    [graphData.relatedConcepts, state.selectedCanonicalId],
  );

  const selectedFrameworks = useMemo(
    () =>
      state.selectedCanonicalId
        ? (graphData.frameworksByCanonical.get(state.selectedCanonicalId) ?? [])
        : [],
    [graphData.frameworksByCanonical, state.selectedCanonicalId],
  );

  // Emit a node-selected + concept-opened pair for any canonical that becomes
  // the active concept. Both galaxy clicks and in-panel navigation route here.
  const trackConceptSelection = useCallback((canonicalId: string) => {
    const canonical = graphData.canonicals.find((c) => c.id === canonicalId);
    if (!canonical) return;
    track('graph_node_selected', { entity_type: 'canonical', name: canonical.name });
    track('graph_concept_opened', { name: canonical.name });
  }, [graphData.canonicals, track]);

  const handleSelectCanonical = useCallback((canonicalId: string) => {
    trackConceptSelection(canonicalId);
    setState({ viewLevel: 'concept', selectedCanonicalId: canonicalId });
  }, [trackConceptSelection]);

  const handleCloseConceptPanel = useCallback(() => {
    setState({ viewLevel: 'galaxy', selectedCanonicalId: null });
  }, []);

  // Navigate between concepts within the panel
  const handleConceptClick = useCallback((canonicalId: string) => {
    trackConceptSelection(canonicalId);
    setState({ viewLevel: 'concept', selectedCanonicalId: canonicalId });
  }, [trackConceptSelection]);

  if (graphData.loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ background: BG_COLOR }}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-how-accent-warm animate-spin" />
          <p className="text-white/50 text-sm font-heading">Loading knowledge graph...</p>
        </div>
      </div>
    );
  }

  if (graphData.error) {
    return (
      <div className="h-full flex items-center justify-center" style={{ background: BG_COLOR }}>
        <p className="text-red-400 text-sm">{graphData.error}</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: BG_COLOR,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <HowieGalaxyView
        canonicals={graphData.canonicals}
        canonicalDomainIndex={graphData.canonicalDomainIndex}
        canonicalLinks={graphData.canonicalLinks}
        onSelectCanonical={handleSelectCanonical}
      />

      {state.viewLevel === 'concept' && selectedCanonical && (
        <HowieConceptPanel
          canonical={selectedCanonical}
          domainIndex={selectedDomainIndex}
          poleInfos={selectedCanonicalPoles}
          relatedConcepts={selectedRelatedConcepts}
          frameworkAssociations={selectedFrameworks}
          onClose={handleCloseConceptPanel}
          onConceptClick={handleConceptClick}
        />
      )}
    </div>
  );
};
